import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {lodash, removeItem} from "../../common/utils";
import {TransitPhase} from '@portbase/bezoekschip-service-typescriptmodels';
import {Transit} from "../transit.utils";

export const transitPhases: TransitPhase[] = [
  TransitPhase.undeclared, TransitPhase.inspection, TransitPhase.insufficientGuarantee, TransitPhase.rejected,
  TransitPhase.releaseRejected, TransitPhase.declared, TransitPhase.sent, TransitPhase.cancellationRequested,
  TransitPhase.accepted, TransitPhase.inTransit, TransitPhase.inTransitMrnSent, TransitPhase.inTransitMrnRejected,
  TransitPhase.inTransitMrnAccepted, TransitPhase.incident, TransitPhase.proofOfArrivalRequested,
  TransitPhase.proofOfArrivalSent, TransitPhase.arrived, TransitPhase.discrepancies, TransitPhase.writtenOff,
  TransitPhase.cancelled];

@Component({
  selector: 'app-transit-filter',
  templateUrl: './transit-filter.component.html',
  styleUrls: ['./transit-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TransitFilterComponent implements TransitFilter, OnInit {
  phaseCounts: { [P in TransitPhase]?: number };
  selectedOptions: TransitPhase[];
  deselectedOptions: TransitPhase[];
  ready: boolean;

  @Input() transits: Transit[];
  @Output() changed = new EventEmitter<TransitFilter>();

  ngOnInit(): void {
    const storedOptions: { selectedOptions, deselectedOptions } = JSON.parse(localStorage.getItem('transit-filter'));
    const selectedOptions = storedOptions?.selectedOptions;
    const deselectedOptions = storedOptions?.deselectedOptions;

    this.phaseCounts = Object.fromEntries(transitPhases.map(transitPhase => [transitPhase, 0]));
    this.selectedOptions = !selectedOptions ? [TransitPhase.undeclared, TransitPhase.inspection,
      TransitPhase.insufficientGuarantee, TransitPhase.rejected, TransitPhase.declared, TransitPhase.sent,
      TransitPhase.cancellationRequested, TransitPhase.incident, TransitPhase.proofOfArrivalRequested,
      TransitPhase.discrepancies] : transitPhases.filter(transitPhase => selectedOptions.includes(transitPhase));
    this.deselectedOptions = !deselectedOptions ? [] :
      transitPhases.filter(transitPhase => deselectedOptions.includes(transitPhase));
    this.ready = true;
    this.changed.emit(this);
  }

  get options(): TransitPhase[] {
    return transitPhases;
  }

  private onChanged() {
    this.changed.emit(this);
    localStorage.setItem('transit-filter', JSON.stringify({
      selectedOptions: this.selectedOptions,
      deselectedOptions: this.deselectedOptions
    }));
  }

  phaseDisplayName(option: TransitPhase) {
    switch (option) {
      case TransitPhase.undeclared:
        return 'Draft';
      case TransitPhase.declared:
        return 'Scheduled';
      case TransitPhase.inTransitMrnSent:
        return 'In Transit MRN Sent';
      case TransitPhase.inTransitMrnAccepted:
        return 'In Transit MRN Acc.';
      case TransitPhase.inTransitMrnRejected:
        return 'In Transit MRN Rej.';
      case TransitPhase.proofOfArrivalRequested:
        return 'Proof Of Arrival Req.';
      case TransitPhase.cancellationRequested:
        return 'Cancellation Req.';
      default:
        return lodash.startCase(option);
    }
  }

  count(option: TransitPhase): number {
    return this.phaseCounts[option];
  }

  isOn(option: TransitPhase): boolean {
    return this.selectedOptions.some(o => option === o);
  }

  isOff(option: TransitPhase): boolean {
    return this.deselectedOptions.some(o => option === o);
  }

  isToggled(option: TransitPhase): boolean {
    return this.isOn(option) || this.isOff(option);
  }

  toggleOn(option: TransitPhase) {
    removeItem(this.deselectedOptions, option);

    if (this.selectedOptions.find(o => option === o)) {
      removeItem(this.selectedOptions, option);
    } else {
      this.selectedOptions.push(option);
    }

    this.onChanged();
  }

  toggleOff(option: TransitPhase) {
    removeItem(this.selectedOptions, option);

    if (this.deselectedOptions.find(o => option === o)) {
      removeItem(this.deselectedOptions, option);
    } else {
      this.deselectedOptions.push(option);
    }

    this.onChanged();
  }

  reset(option: TransitPhase) {
    removeItem(this.selectedOptions, option);
    removeItem(this.deselectedOptions, option);

    this.onChanged();
  }

  resetAll() {
    this.selectedOptions = [];
    this.deselectedOptions = [];
    this.onChanged();
  }
}

export interface TransitFilter {
  phaseCounts: { [P in TransitPhase]?: number };
  selectedOptions: TransitPhase[];
  deselectedOptions: TransitPhase[];
  ready: boolean;
}
